import { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";

const WEEKDAY_KEYS = [0, 1, 2, 3, 4, 5, 6];

export default function AdminAvailability() {
  const { t } = useLanguage();
  const [hours, setHours] = useState([]);
  const [breaks, setBreaks] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [newBreak, setNewBreak] = useState({ weekday: 1, start_time: "13:00", end_time: "13:30", label: "" });
  const [newBlocked, setNewBlocked] = useState({ start_date: "", end_date: "", is_full_day: true, start_time: "", end_time: "", reason: "" });
  const [saved, setSaved] = useState(false);

  const loadHours = () => api.get("/admin/working-hours").then(({ data }) => setHours(data)).catch(() => {});
  const loadBreaks = () => api.get("/admin/breaks").then(({ data }) => setBreaks(data)).catch(() => {});
  const loadBlocked = () => api.get("/admin/blocked-times").then(({ data }) => setBlocked(data)).catch(() => {});

  useEffect(() => { loadHours(); loadBreaks(); loadBlocked(); }, []);

  const updateHour = (weekday, field, value) => {
    setHours(hours.map((h) => (h.weekday === weekday ? { ...h, [field]: value } : h)));
  };

  const saveHours = async () => {
    await api.put("/admin/working-hours", hours.map(({ weekday, is_open, start_time, end_time }) => ({ weekday, is_open, start_time, end_time })));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const addBreak = async () => {
    await api.post("/admin/breaks", newBreak);
    setNewBreak({ weekday: 1, start_time: "13:00", end_time: "13:30", label: "" });
    loadBreaks();
  };
  const deleteBreak = async (id) => { await api.delete(`/admin/breaks/${id}`); loadBreaks(); };

  const addBlocked = async () => {
    if (!newBlocked.start_date || !newBlocked.end_date) return;
    await api.post("/admin/blocked-times", newBlocked);
    setNewBlocked({ start_date: "", end_date: "", is_full_day: true, start_time: "", end_time: "", reason: "" });
    loadBlocked();
  };
  const deleteBlocked = async (id) => { await api.delete(`/admin/blocked-times/${id}`); loadBlocked(); };

  return (
    <div data-testid="admin-availability-page">
      <h1 className="font-serif text-2xl sm:text-3xl text-white mb-8">{t("admin.availability.title")}</h1>

      <section className="mb-10">
        <h2 className="text-white text-lg mb-4">{t("admin.availability.workingHours")}</h2>
        <div className="bg-[#141414] border border-white/10 rounded-lg divide-y divide-white/5">
          {hours.map((h) => (
            <div key={h.weekday} data-testid={`working-hour-row-${h.weekday}`} className="flex flex-wrap items-center gap-4 p-4">
              <span className="w-28 text-white/80 text-sm">{t(`hours.days.${h.weekday}`)}</span>
              <Switch data-testid={`working-hour-toggle-${h.weekday}`} checked={h.is_open} onCheckedChange={(v) => updateHour(h.weekday, "is_open", v)} className="data-[state=checked]:bg-gold" />
              <span className="text-white/40 text-xs">{h.is_open ? t("admin.availability.open") : t("admin.availability.closed")}</span>
              {h.is_open && (
                <>
                  <Input data-testid={`working-hour-start-${h.weekday}`} type="time" value={h.start_time || ""} onChange={(e) => updateHour(h.weekday, "start_time", e.target.value)} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-32" />
                  <Input data-testid={`working-hour-end-${h.weekday}`} type="time" value={h.end_time || ""} onChange={(e) => updateHour(h.weekday, "end_time", e.target.value)} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-32" />
                </>
              )}
            </div>
          ))}
        </div>
        <Button data-testid="save-working-hours-button" onClick={saveHours} className="mt-4 bg-gold text-white hover:bg-gold-hover rounded-none">
          {saved ? t("common.saved") : t("common.save")}
        </Button>
      </section>

      <section className="mb-10">
        <h2 className="text-white text-lg mb-4">{t("admin.availability.breaks")}</h2>
        <div className="bg-[#141414] border border-white/10 rounded-lg divide-y divide-white/5 mb-4">
          {breaks.length === 0 && <p className="p-4 text-white/40 text-sm">{t("admin.availability.noBreaks")}</p>}
          {breaks.map((b) => (
            <div key={b.id} data-testid={`break-row-${b.id}`} className="flex items-center justify-between p-4 text-sm">
              <span className="text-white/80">{t(`hours.days.${b.weekday}`)}: {b.start_time} - {b.end_time} {b.label && `(${b.label})`}</span>
              <button data-testid={`break-delete-${b.id}`} onClick={() => deleteBreak(b.id)} className="text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Select value={String(newBreak.weekday)} onValueChange={(v) => setNewBreak({ ...newBreak, weekday: Number(v) })}>
            <SelectTrigger data-testid="new-break-weekday" className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-36"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
              {WEEKDAY_KEYS.map((w) => <SelectItem key={w} value={String(w)}>{t(`hours.days.${w}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="new-break-start" type="time" value={newBreak.start_time} onChange={(e) => setNewBreak({ ...newBreak, start_time: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-32" />
          <Input data-testid="new-break-end" type="time" value={newBreak.end_time} onChange={(e) => setNewBreak({ ...newBreak, end_time: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-32" />
          <Input data-testid="new-break-label" placeholder={t("admin.availability.label")} value={newBreak.label} onChange={(e) => setNewBreak({ ...newBreak, label: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-40" />
          <Button data-testid="add-break-button" onClick={addBreak} className="bg-gold text-white hover:bg-gold-hover rounded-none"><Plus size={16} className="mr-1.5" />{t("admin.availability.addBreak")}</Button>
        </div>
      </section>

      <section>
        <h2 className="text-white text-lg mb-4">{t("admin.availability.blockedTimes")}</h2>
        <div className="bg-[#141414] border border-white/10 rounded-lg divide-y divide-white/5 mb-4">
          {blocked.length === 0 && <p className="p-4 text-white/40 text-sm">{t("admin.availability.noBlocked")}</p>}
          {blocked.map((b) => (
            <div key={b.id} data-testid={`blocked-row-${b.id}`} className="flex items-center justify-between p-4 text-sm">
              <span className="text-white/80">{b.start_date} → {b.end_date} {b.is_full_day ? `(${t("admin.availability.fullDay")})` : `(${b.start_time}-${b.end_time})`} {b.reason && `— ${b.reason}`}</span>
              <button data-testid={`blocked-delete-${b.id}`} onClick={() => deleteBlocked(b.id)} className="text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Input data-testid="new-blocked-start-date" type="date" value={newBlocked.start_date} onChange={(e) => setNewBlocked({ ...newBlocked, start_date: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-40" />
          <Input data-testid="new-blocked-end-date" type="date" value={newBlocked.end_date} onChange={(e) => setNewBlocked({ ...newBlocked, end_date: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-40" />
          <div className="flex items-center gap-2">
            <Checkbox data-testid="new-blocked-full-day" checked={newBlocked.is_full_day} onCheckedChange={(v) => setNewBlocked({ ...newBlocked, is_full_day: v })} className="border-white/30 data-[state=checked]:bg-gold" />
            <Label className="text-white/70 text-sm">{t("admin.availability.fullDay")}</Label>
          </div>
          {!newBlocked.is_full_day && (
            <>
              <Input data-testid="new-blocked-start-time" type="time" value={newBlocked.start_time} onChange={(e) => setNewBlocked({ ...newBlocked, start_time: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-32" />
              <Input data-testid="new-blocked-end-time" type="time" value={newBlocked.end_time} onChange={(e) => setNewBlocked({ ...newBlocked, end_time: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-32" />
            </>
          )}
          <Input data-testid="new-blocked-reason" placeholder={t("admin.availability.reason")} value={newBlocked.reason} onChange={(e) => setNewBlocked({ ...newBlocked, reason: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none w-48" />
          <Button data-testid="add-blocked-button" onClick={addBlocked} className="bg-gold text-white hover:bg-gold-hover rounded-none"><Plus size={16} className="mr-1.5" />{t("admin.availability.addBlocked")}</Button>
        </div>
      </section>
    </div>
  );
}
